import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { contentService } from '../services/content';
import { Beat, Verse } from '../types/content';
import { useAuth } from '../context/AuthContext';

export interface UserProfileData {
  id: string;
  username: string;
  role: 'producer' | 'rapper' | 'producer_rapper' | string | string[];
  state: string;
  avatar_url?: string;
  bio?: string;
  created_at: string;
  instagram?: string;
  youtube?: string;
}

export interface UserProfileStats {
  totalBeats: number;
  totalVerses: number;
  totalLikes: number;
}

export function useUserProfile() {
  const { userId } = useParams<{ userId: string }>();
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [beats, setBeats] = useState<Beat[]>([]);
  const [verses, setVerses] = useState<Verse[]>([]);
  const [stats, setStats] = useState<UserProfileStats>({ totalBeats: 0, totalVerses: 0, totalLikes: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { authState } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editSuccess, setEditSuccess] = useState<string | null>(null);

  const viewerId = authState.user?.id;

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single();

        if (profileError) throw profileError;
        if (!profileData) throw new Error('Profile not found');

        setProfile(profileData);

        let beatsData: Beat[] = [];
        if (profileData.role === 'producer' || profileData.role === 'producer_rapper') {
          const { data, error: beatsError } = await supabase
            .from('beats')
            .select(`
              *,
              producer:profiles!producer_id(*),
              beat_id:id
            `)
            .eq('producer_id', userId)
            .order('created_at', { ascending: false });
          if (beatsError) throw beatsError;
          beatsData = data || [];

          if (beatsData.length > 0) {
            const beatIds = beatsData.map(b => b.id);
            const { data: beatVotes, error: beatVotesError } = await supabase
              .from('votes')
              .select('*')
              .in('content_id', beatIds)
              .eq('content_type', 'beat');
            if (beatVotesError) throw beatVotesError;
            beatsData = beatsData.map(beat => {
              const votes = (beatVotes || []).filter(v => v.content_id === beat.id);
              const vote_count = votes.reduce((acc, v) => acc + (v.vote || 0), 0);
              return { ...beat, vote_count };
            });
          }
          setBeats(beatsData);
        }

        let versesData: Verse[] = [];
        if (profileData.role === 'rapper' || profileData.role === 'producer_rapper') {
          // A profile is a showcase: pending and declined takes belong to the
          // owner's own view and the producer's review queue, not here.
          let versesQuery = supabase
            .from('verses')
            .select(`
              *,
              rapper:profiles!rapper_id(*),
              beat:beats(*, producer:profiles!producer_id(*))
            `)
            .eq('rapper_id', userId);

          if (viewerId !== userId) {
            versesQuery = versesQuery.eq('status', 'approved');
          }

          const { data, error: versesError } = await versesQuery.order('created_at', {
            ascending: false,
          });
          if (versesError) throw versesError;
          versesData = data || [];

          if (versesData.length > 0) {
            const verseIds = versesData.map(v => v.id);
            const { data: verseVotes, error: verseVotesError } = await supabase
              .from('votes')
              .select('*')
              .in('content_id', verseIds)
              .eq('content_type', 'verse');
            if (verseVotesError) throw verseVotesError;
            versesData = versesData.map(verse => {
              const votes = (verseVotes || []).filter(v => v.content_id === verse.id);
              const vote_count = votes.reduce((acc, v) => acc + (v.vote || 0), 0);
              return { ...verse, vote_count };
            });
          }
          setVerses(versesData);
        }

        const userIdAsUuid = userId ? userId.toString() : null;
        const { data: statsData, error: statsError } = await supabase.rpc('get_user_stats', {
          user_id: userIdAsUuid,
        });

        if (statsError) throw statsError;
        setStats(statsData || { totalBeats: 0, totalVerses: 0, totalLikes: 0 });
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Error loading profile. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [userId, viewerId]);

  const handleEditSubmit = async (formData: Partial<UserProfileData>) => {
    if (!profile) return;

    setEditLoading(true);
    setEditError(null);
    setEditSuccess(null);
    try {
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          username: formData.username,
          instagram: formData.instagram,
          youtube: formData.youtube,
          updated_at: new Date().toISOString(),
        })
        .eq('id', profile.id);
      if (updateError) throw updateError;
      setProfile({ ...profile, ...formData });
      setEditSuccess('Profile updated successfully!');

      setTimeout(() => {
        setIsModalOpen(false);
        setEditSuccess(null);
      }, 1500);
    } catch {
      setEditError('Failed to update profile.');
    } finally {
      setEditLoading(false);
    }
  };

  const isOwnProfile = authState.user?.id === profile?.id;

  /** A rapper can pull down their own take, published or not. */
  const removeVerse = useCallback(
    async (verseId: string) => {
      const verse = verses.find(v => v.id === verseId);
      if (!verse) return;
      await contentService.deleteVerse(verse);
      setVerses(prev => prev.filter(v => v.id !== verseId));
      setStats(prev => ({ ...prev, totalVerses: Math.max(0, prev.totalVerses - 1) }));
    },
    [verses]
  );

  const role = profile?.role;
  const showBeats =
    role === 'producer' ||
    role === 'producer_rapper' ||
    (Array.isArray(role) && (role.includes('producer') || role.includes('producer_rapper')));

  const showVerses =
    role === 'rapper' ||
    role === 'producer_rapper' ||
    (Array.isArray(role) && (role.includes('rapper') || role.includes('producer_rapper')));

  const showShows =
    role === 'producer' ||
    role === 'rapper' ||
    role === 'producer_rapper' ||
    (Array.isArray(role) &&
      (role.includes('producer') || role.includes('rapper') || role.includes('producer_rapper')));

  const formatRoleLabel = (role: UserProfileData['role']) => {
    const pretty = (r: string) =>
      r === 'producer_rapper' ? 'Producer & Rapper' : r ? `${r.charAt(0).toUpperCase()}${r.slice(1)}` : '';
    if (Array.isArray(role)) {
      return role.map(pretty).join(' / ');
    }
    return pretty(role || '');
  };

  return {
    userId,
    profile,
    beats,
    verses,
    stats,
    loading,
    error,
    isModalOpen,
    setIsModalOpen,
    editLoading,
    editError,
    editSuccess,
    handleEditSubmit,
    isOwnProfile,
    showBeats,
    showVerses,
    showShows,
    formatRoleLabel,
    removeVerse,
  };
}
